import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  SecurityContext,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FacebookAdPreviewComponent } from '../facebook-ad-preview/facebook-ad-preview.component';
import { GoogleAdPreviewComponent } from '../google-ad-preview/google-ad-preview.component';
import { EmailPreviewComponent } from '../email-preview/email-preview.component';
import { EmailPreviewModalComponent } from '../email-preview-modal/email-preview-modal.component';
import { Campaign, getCampaignPlatformIds } from '../../services/campaign.service';
import {
  EmailPreviewService,
  EmailData,
  DEFAULT_EMAIL_DATA,
} from '../../services/email-preview.service';

declare let SegmentedMessage: any;

/**
 * Displays previews and controls for the various pieces of campaign content (email, SMS, Facebook, Google Ads) and provides actions to preview, edit, expand, or view content in the browser.
 * @component
 * @selector app-campaign-content
 * @example
 *   <app-campaign-content [campaign]="sampleCampaign"></app-campaign-content>
 * @input {Campaign} campaign - The campaign model containing platforms, contents (email, sms, facebook, google), promo code, location and identifying metadata. When this input changes the component updates its internal preview email data.
 * @output {none} none - This component does not emit Angular output events; user interactions navigate via the Router or update shared services (e.g. EmailPreviewService) and local modal state.
 *
 * Key responsibilities:
 * - Render preview data for email, SMS, Facebook and Google Ads based on the provided campaign.
 * - Provide actions to preview content, navigate to the campaign editor on edit, expand the email preview in a modal, and open an email preview in a new browser tab.
 * - Build and maintain a sampleEmailData object from campaign.contents.email for preview and modal use.
 *
 * Important inputs/outputs:
 * - campaign (Input): Triggers data refresh when changed; used throughout to determine which platforms and content exist.
 *
 * Important lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): Listens for changes to the campaign input and calls updateEmailData() when campaign changes.
 *
 * Key methods:
 * - private updateEmailData(): Extracts email-specific fields from campaign.contents.email and populates sampleEmailData used by the email preview and modal.
 * - get contentData(): Computed getter that returns structured preview metadata for email, textMessage, facebookAd and googleAd, using sensible defaults when campaign or fields are missing.
 * - onPreviewClick(contentType: string): (Placeholder) Intended to trigger preview functionality for the given content type.
 * - onEditClick(contentType: string): Navigates to the campaign editor and selects the appropriate tab for editing the chosen content type.
 * - onExpandClick(contentType: string): Opens the email preview modal (sets isEmailModalOpen = true) — currently used for email expansion.
 * - onCloseEmailModal(): Closes the email modal (sets isEmailModalOpen = false).
 * - onViewInBrowser(): Stores sampleEmailData in EmailPreviewService and opens the email preview route in a new browser tab.
 * - formatTextMessage(message: string): Converts newline characters to <br> and sanitizes the HTML for safe display.
 * - isPlatformSelected(platform: string): Returns whether a platform is included in campaign.platforms.
 * - hasContent(type: string): Checks if meaningful content exists for a given content type (email, sms, facebook, google_ads) to decide whether to show previews.
 * - hasAnyContent(): Returns true if any selected platform contains content and thus previews should be shown; handles empty platform selections.
 * - onEditPlatform(): Navigates to the campaign editor's Platform section for changing selected platforms.
 *
 * Notes:
 * - The component is standalone and imports several preview components and Angular CommonModule.
 * - It relies on Router for navigation, EmailPreviewService for sharing preview data, and DomSanitizer for safely rendering formatted SMS text.
 */
@Component({
  selector: 'app-campaign-content',
  standalone: true,
  imports: [
    CommonModule,
    FacebookAdPreviewComponent,
    GoogleAdPreviewComponent,
    EmailPreviewComponent,
    EmailPreviewModalComponent,
  ],
  templateUrl: './campaign-content.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-content.component.scss'],
})
export class CampaignContentComponent implements OnChanges {
  @Input() campaign!: Campaign;

  // Modal state
  isEmailModalOpen = false;

  // Email data property that gets updated when campaign changes
  sampleEmailData: EmailData = { ...DEFAULT_EMAIL_DATA };

  constructor(
    private router: Router,
    private emailPreviewService: EmailPreviewService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When campaign input changes, update the email data
    if (changes['campaign'] && this.campaign) {
      this.updateEmailData();
    }
  }

  // Update email data from campaign data
  /**
   * Update the component's sampleEmailData from this.campaign.contents.email if present.
   * @example
   * updateEmailData()
   * // sampleEmailData is populated with mapped email fields (subject, fromEmail, logo, image, header, body texts, button settings, footer info)
   * @param {void} none - No arguments.
   * @returns {void} Does not return a value; updates this.sampleEmailData or returns early if email content is missing.
   **/
  private updateEmailData(): void {
    if (!this.campaign || !this.campaign.contents || !this.campaign.contents.email) {
      return;
    }

    const emailContent = this.campaign.contents.email;

    this.sampleEmailData = {
      // Basic Info
      subject: emailContent.email_subject || '',
      fromEmail: emailContent.email_from_id || '',

      // Header
      logo: emailContent.email_logo_img || null,
      logoUrl:
        typeof emailContent.email_logo_img === 'string' ? emailContent.email_logo_img : undefined,

      // Body
      image: emailContent.email_body_img || null,
      imageUrl:
        typeof emailContent.email_body_img === 'string' ? emailContent.email_body_img : undefined,
      header: emailContent.email_header || '',
      bodyText: emailContent.email_body_text || '',
      body2Text: emailContent.email_body_text2 || '',
      body3Text: emailContent.email_body_text3 || '',
      body4Text: emailContent.email_body_text4 || '',

      // Button
      buttonText: emailContent.email_button_text || '',
      buttonPosition: (emailContent.email_button_position as 'top' | 'bottom') || 'bottom',
      buttonColor: emailContent.email_button_color || '#326FD1',

      // Footer
      carWashName: emailContent.email_footer_name || '',
      street: emailContent.email_footer_street || '',
      city: emailContent.email_footer_city || '',
      state: emailContent.email_footer_state || '',
      zipCode: emailContent.email_footer_zip || '',
      email: emailContent.email_reply_to || '',
      phoneNumber: emailContent.email_footer_phone || '',
    };
  }

  // Generate content data from campaign data
  /**
   * Builds and returns structured preview data for campaign content (email, SMS, Facebook ad, Google ad).
   * @example
   * contentData()
   * { email: { title: 'Subject: SAVE20 - Car Wash Special - Limited Time Offer!', subtitle: 'From: example@domain.com', previewImage: 'assets/images/email-preview.png' }, textMessage: { title: 'Your Text Message Preview', message: '...', linkText: '...' }, facebookAd: { title: 'Your Facebook Ad Preview', name: '...', caption: '...', headline: '...', buttonText: '...', logo: null, image: null, logoUrl: '', imageUrl: '' }, googleAd: { image: '', imageUrl: '', title: '', company: '' } }
   * @param {{Object}} this - Component instance containing an optional campaign property used to build the preview data.
   * @returns {{Object}} An object with keys email, textMessage, facebookAd, and googleAd containing preview values.
   **/
  get contentData() {
    if (!this.campaign) {
      return {
        email: { title: '', subtitle: '', previewImage: '' },
        textMessage: { title: '', message: '', linkText: '' },
        facebookAd: {
          title: '',
          name: '',
          caption: '',
          headline: '',
          buttonText: '',
          logo: null,
          image: null,
          logoUrl: '',
          imageUrl: '',
        },
        googleAd: { image: '', imageUrl: '', title: '', company: '' },
      };
    }

    const campaignName = this.campaign.name || 'Car Wash Special';
    const promoCode = this.campaign.promo_code || 'SAVE20';

    return {
      email: {
        title: `Subject: ${promoCode} - ${campaignName} - Limited Time Offer!`,
        subtitle: `From: ${this.campaign.contents?.email?.email_from_id}`,
        previewImage: 'assets/images/email-preview.png',
      },
      textMessage: {
        title: 'Your Text Message Preview',
        message: this.campaign.contents?.sms?.sms_body || '',
        linkText: this.campaign.contents?.sms?.sms_link || '',
      },
      facebookAd: {
        title: 'Your Facebook Ad Preview',
        name: this.campaign.contents?.facebook?.fb_name || '',
        caption: this.campaign.contents?.facebook?.fb_caption || '',
        headline: this.campaign.contents?.facebook?.fb_headline || '',
        buttonText: this.campaign.contents?.facebook?.fb_button_text || '',
        logo: null,
        image: null,
        logoUrl: this.campaign.contents?.facebook?.fb_logo_img || '',
        imageUrl: this.campaign.contents?.facebook?.fb_img || '',
      },
      googleAd: {
        image: this.campaign.contents?.google?.g_ad_img || '',
        imageUrl: this.campaign.contents?.google?.g_ad_img || '',
        title: this.campaign.contents?.google?.g_ad_title || '',
        company: this.campaign.contents?.google?.g_ad_company || '',
      },
    };
  }

  onPreviewClick(contentType: string): void {
    // Implement preview functionality
  }

  /**
   * Navigate to the campaign editor and open the content tab corresponding to the provided content type.
   * @example
   * nEditClick('email')
   * undefined
   * @param {{string}} {{contentType}} - The content type indicating which editor tab to open (e.g., 'email', 'textMessage', 'facebookAd', 'googleAd').
   * @returns {{void}} No return value; navigates to the campaign editor route with the selected tab and campaignId query parameter.
   **/
  onEditClick(contentType: string): void {
    // Navigate to campaign editor with appropriate tab based on content type
    let tab = 'content';
    if (contentType === 'email') {
      tab = 'Email';
    } else if (contentType === 'textMessage') {
      tab = 'Text Message';
    } else if (contentType === 'facebookAd') {
      tab = 'Facebook';
    } else if (contentType === 'googleAd') {
      tab = 'Google Ads';
    }

    this.router.navigate(['/campaign-editor/content/', tab], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  onExpandClick(contentType: string): void {
    this.isEmailModalOpen = true;
  }

  onCloseEmailModal(): void {
    this.isEmailModalOpen = false;
  }

  onViewInBrowser(): void {
    // Store email data in service
    this.emailPreviewService.setEmailData(this.sampleEmailData);

    // Get the current URL and construct the new route
    const url = this.router.serializeUrl(this.router.createUrlTree(['/email-preview']));

    // Open in new tab
    window.open(url, '_blank');
  }

  formatTextMessage(message: string): SafeHtml {
    if (!message) {
      return '';
    }
    // Replace newlines with <br> tags
    const formatted = message.replace(/\n/g, '<br>');
    // Sanitize the HTML to prevent XSS attacks
    return this.sanitizer.sanitize(SecurityContext.HTML, formatted) || '';
  }

  // Check if platform is selected
  isPlatformSelected(platform: string): boolean {
    return getCampaignPlatformIds(this.campaign?.platforms).includes(platform);
  }

  private findMmsCampaignContent(contents: any): any {
    if (!contents) {
      return null;
    }
    if (Array.isArray(contents)) {
      return contents.find((item: any) => item?.platform === 'mms_campaign') || null;
    }
    const record = contents as Record<string, any>;
    return (record['mms_campaign'] as any) || null;
  }

  /**
   * Builds display-ready MMS campaign preview data (image, caption, url, fallback, second bubble) from campaign.contents.mms_campaign.
   * @returns {object|null} The mapped MMS preview fields, or null when no mms_campaign content exists.
   **/
  get mmsCampaignPreview() {
    const mmsContent = this.findMmsCampaignContent(this.campaign?.contents);
    if (!mmsContent) {
      return null;
    }

    const cfg = mmsContent.mms_config || {};
    const bubble1 = cfg.bubble_1_coupon || {};
    const fallback = cfg.mms_fallback || {};

    return {
      imageUrl: bubble1.coupon_image_url || '',
      altText: bubble1.image_alt_text || '',
      caption: bubble1.image_caption || '',
      url: cfg.mms_link || '',
      fallbackText: fallback.text || '',
      fallbackBehavior: fallback.behavior || '',
      bubble2Text: cfg.bubble_2_text_body || '',
      splitTwoBubbles: Boolean(cfg.bubble_2_text_body?.trim()),
    };
  }

  hasMmsCampaignData(): boolean {
    const preview = this.mmsCampaignPreview;
    if (!preview) {
      return false;
    }
    return Boolean(
      preview.imageUrl.trim() || preview.caption.trim() || preview.fallbackText.trim()
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildSegmentParts(text: string): string[] {
    if (!text || !text.trim()) return [''];
    try {
      if (typeof SegmentedMessage !== 'undefined') {
        const msg = new SegmentedMessage(text);
        if (msg.segmentsCount <= 1) return [text];
        return (msg.segments as any[][]).map((seg: any[]) => seg.map((c: any) => c.raw).join(''));
      }
    } catch (_) {
      /* fallback below */
    }
    return [text];
  }

  get smsSegmentParts(): string[] {
    const msg = this.contentData.textMessage.message || '';
    const link = this.contentData.textMessage.linkText || '';
    const full = msg + (link.trim() ? '\n\n' + link : '');
    return this.buildSegmentParts(full);
  }

  get mmsCaptionSegmentParts(): string[] {
    return this.buildSegmentParts(this.mmsCampaignPreview?.caption || '');
  }

  get mmsBubble2SegmentParts(): string[] {
    return this.buildSegmentParts(this.mmsCampaignPreview?.bubble2Text || '');
  }

  // Check if content exists for a given type
  /**
   * Returns true if the campaign has non-empty content for the given type (email, sms, facebook, or google_ads).
   * @example
   * asContent('email')
   * true
   * @param {{string}} {{type}} - The content type to check: 'email', 'sms', 'facebook', or 'google_ads'.
   * @returns {{boolean}} Whether the campaign has non-empty content for the specified type.
   **/
  hasContent(type: string): boolean {
    if (!this.campaign?.contents) {
      return false;
    }

    if (type === 'email') {
      const emailContent = this.campaign.contents.email;
      if (emailContent) {
        return (
          (emailContent.email_subject && emailContent.email_subject.trim() !== '') ||
          (emailContent.email_header && emailContent.email_header.trim() !== '') ||
          (emailContent.email_body_text && emailContent.email_body_text.trim() !== '') ||
          (emailContent.email_footer_name && emailContent.email_footer_name.trim() !== '')
        );
      }
      return false;
    }

    if (type === 'sms') {
      const smsContent = this.campaign.contents.sms;
      if (smsContent && smsContent.sms_body && smsContent.sms_body.trim() !== '') {
        return true;
      }
      return this.hasMmsCampaignData();
    }

    if (type === 'facebook') {
      const facebookContent = this.campaign.contents.facebook;
      if (facebookContent) {
        return facebookContent.fb_caption && facebookContent.fb_caption.trim() !== '';
      }
      return false;
    }

    if (type === 'google_ads') {
      const googleContent = this.campaign.contents.google;
      if (googleContent) {
        return (
          (googleContent.g_ad_title && googleContent.g_ad_title.trim() !== '') ||
          (googleContent.g_ad_company && googleContent.g_ad_company.trim() !== '')
        );
      }
      return false;
    }

    return false;
  }

  // Check if any content exists for any platform
  /**
   * Determine whether any selected campaign platform has content.
   * @example
   * asAnyContent()
   * true
   * @returns {boolean} True if any selected platform has content; otherwise false.
   **/
  hasAnyContent(): boolean {
    if (!this.campaign) {
      return false;
    }

    const platformIds = getCampaignPlatformIds(this.campaign.platforms);
    if (!platformIds.length) {
      return false;
    }

    return platformIds.some(platform => {
      if (platform === 'email') {
        return this.hasContent('email');
      } else if (platform === 'sms') {
        return this.hasContent('sms');
      } else if (platform === 'facebook') {
        return this.hasContent('facebook');
      } else if (platform === 'google') {
        return this.hasContent('google_ads');
      }
      return false;
    });
  }

  // Navigate to platform section in edit campaign
  onEditPlatform(): void {
    if (this.campaign?.id) {
      this.router.navigate(['/campaign-editor', 'overview', 'Platform'], {
        queryParams: { campaignId: this.campaign.id },
      });
    }
  }
}
